import { useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, Modal, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  APIProvider,
  Map,
  Marker,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { boxModalStyle } from "../../BookingStyle";
import { GOOGLE_MAPS_API_KEY } from "../../../../../../helpers/googleMaps/GoogleMapsLoader";

const DEFAULT_CENTER = { lat: -12.046374, lng: -77.042793 };

const hasValidPoint = (point) =>
  Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng));

const getInitialMarker = (coordinate) =>
  hasValidPoint(coordinate)
    ? { lat: Number(coordinate.lat), lng: Number(coordinate.lng) }
    : null;

const formatCoordinateAddress = (point) =>
  `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`;

const isCoordinateAddress = (value) =>
  /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(String(value || "").trim());

const OriginMap = ({ address, coordinate, onAccept }) => {
  const geocodingLibrary = useMapsLibrary("geocoding");
  const geocoder = useMemo(
    () => geocodingLibrary && new geocodingLibrary.Geocoder(),
    [geocodingLibrary],
  );
  const [mapAddress, setMapAddress] = useState(address);
  const [markerPosition, setMarkerPosition] = useState(() =>
    getInitialMarker(coordinate),
  );

  useEffect(() => {
    setMapAddress(address);
    setMarkerPosition(getInitialMarker(coordinate));
  }, [address, coordinate]);

  useEffect(() => {
    const marker = getInitialMarker(coordinate);
    if (!marker || !geocoder) return;
    if (String(address || "").trim() && !isCoordinateAddress(address)) return;

    const resolveInitialAddress = async () => {
      try {
        const result = await geocoder.geocode({ location: marker });
        const resolvedAddress = result.results?.[0]?.formatted_address;
        if (resolvedAddress) {
          setMapAddress(resolvedAddress);
        }
      } catch (error) {
        console.log(error);
      }
    };

    resolveInitialAddress();
  }, [address, coordinate, geocoder]);

  const handleMapClick = async (event) => {
    const latLng = event.detail.latLng;
    if (!latLng) return;

    const nextPoint = {
      lat: latLng.lat,
      lng: latLng.lng,
    };
    const coordinateAddress = formatCoordinateAddress(nextPoint);

    setMarkerPosition(nextPoint);
    setMapAddress(coordinateAddress);

    if (!geocoder) return;

    try {
      const result = await geocoder.geocode({ location: nextPoint });
      const address = result.results?.[0]?.formatted_address;
      if (address) {
        setMapAddress(address);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleAccept = () => {
    const acceptedAddress = String(mapAddress || "").trim();
    if (!markerPosition || !acceptedAddress || isCoordinateAddress(mapAddress)) {
      return;
    }
    onAccept({
      address: acceptedAddress,
      point: markerPosition,
    });
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Map
          defaultCenter={markerPosition || DEFAULT_CENTER}
          defaultZoom={15}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          onClick={handleMapClick}
          style={{ width: "100%", height: "100%" }}
        >
          {markerPosition && <Marker position={markerPosition} />}
        </Map>
      </Box>
      <TextField
        size="small"
        value={mapAddress}
        placeholder="Seleccione una ubicacion en el mapa"
        InputProps={{ readOnly: true }}
        sx={{
          position: "absolute",
          top: 14,
          left: 14,
          width: {
            xs: "calc(100% - 72px)",
            sm: "70%",
          },
          maxWidth: {
            xs: "calc(100% - 72px)",
            sm: "70%",
          },
          bgcolor: "background.paper",
          borderRadius: "4px",
          boxShadow: 3,
        }}
      />
      <Button
        variant="contained"
        color="primary"
        disabled={
          !markerPosition ||
          !String(mapAddress || "").trim() ||
          isCoordinateAddress(mapAddress)
        }
        onClick={handleAccept}
        sx={{
          position: "absolute",
          right: 14,
          bottom: 14,
          minWidth: 140,
          height: 44,
          fontWeight: "bold",
          boxShadow: 3,
        }}
      >
        Aceptar
      </Button>
    </Box>
  );
};

const OriginMapModal = ({
  open,
  onClose,
  addressOrigin,
  coordOrigin,
  address,
  coordinate,
  onAddressChange,
  onCoordinateChange,
  onLocationAccept,
}) => {
  const mapAddress = address ?? addressOrigin;
  const mapCoordinate = coordinate ?? coordOrigin;

  const handleAcceptLocation = ({ address, point }) => {
    if (onLocationAccept) {
      onLocationAccept({ address, point });
    } else {
      onAddressChange(address);
      onCoordinateChange(point);
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          ...boxModalStyle,
          width: {
            xs: "95%",
            sm: "95%",
          },
          height: "95vh",
          p: 0,
          overflow: "hidden",
        }}
      >
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            width: 34,
            height: 34,
            bgcolor: "error.main",
            color: "common.white",
            zIndex: 1,
            boxShadow: 3,
            "&:hover": {
              bgcolor: "error.dark",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <APIProvider
          apiKey={GOOGLE_MAPS_API_KEY}
          libraries={["geocoding"]}
          onError={(error) => {
            console.log(error);
          }}
        >
          <OriginMap
            address={mapAddress}
            coordinate={mapCoordinate}
            onAccept={handleAcceptLocation}
          />
        </APIProvider>
      </Box>
    </Modal>
  );
};

export default OriginMapModal;
